import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Shipment } from '../models/shipment';
import { ShipmentRequest } from '../models/shipment-request';
import { ShipmentBooking } from '../models/shipment-booking';

@Injectable({
  providedIn: 'root',
})
export class ShipmentsApiService {
  private readonly baseUrl = environment.backendConfig.apiUrl;
  private readonly httpClient = inject(HttpClient);
  private readonly _activeShipment = signal<Shipment | null>(null);

  public isLoading = signal<boolean>(false);

  getAssignedShipment(courier: string): Observable<Shipment> {
    this.isLoading.set(true);
    return this.httpClient.get<Shipment>(`${this.baseUrl}/shipments/${courier.toLowerCase()}/assigned`).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((error) => {
        this.isLoading.set(false);
        throw error;
      })
    );
  }

  getTrackedShipments(filter: 'all' | 'my'): Observable<Shipment[]> {
    this.isLoading.set(true);
    return this.httpClient.get<Shipment[]>(`${this.baseUrl}/shipments?filter=${filter}`).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((error) => {
        this.isLoading.set(false);
        throw error;
      })
    );
  }

  bookShipment(booking: ShipmentBooking): Observable<Shipment> {
    this.isLoading.set(true);
    return this.httpClient.post<Shipment>(`${this.baseUrl}/shipments`, booking).pipe(
      tap(shipment => {
        this.isLoading.set(false);
        this._activeShipment.set(shipment);
      }),
      catchError((error) => {
        this.isLoading.set(false);
        throw error;
      })
    );
  }

  getShipment(courier: string, shipmentId: string): Observable<Shipment> {
    this.isLoading.set(true);
    return this.httpClient.get<Shipment>(`${this.baseUrl}/shipments/${courier.toLowerCase()}/${shipmentId}`).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((error) => {
        this.isLoading.set(false);
        throw error;
      })
    );
  }

  updateShipment(courier: string, shipmentId: string, data: ShipmentRequest): Observable<Shipment> {
    this.isLoading.set(true);
    return this.httpClient.patch<Shipment>(`${this.baseUrl}/shipments/${courier.toLowerCase()}/${shipmentId}`, data).pipe(
      tap(() => this.isLoading.set(false)),
      catchError((error) => {
        this.isLoading.set(false);
        throw error;
      })
    );
  }
}
